import { Component, Input, OnInit } from '@angular/core';
import { FormGroup, FormBuilder, Validators, FormControl, ValidatorFn } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { EformService } from "../../../service/eform.service";

@Component({
   selector: 'app-table',
   templateUrl: './table.component.html',
   styleUrls: ['./table.component.scss']
})
export class TableComponent implements OnInit {
   @Input() field: any = {};
   @Input() form: FormGroup;
   @Input() checkValidate: any;
   @Input() fieldName: any;
   @Input() placeholder: any;
   @Input() labelName: any;
   @Input() isFormName: any;
   @Input() isViewOnly: boolean = false;
   @Input() classes: any;
   @Input() haveSign: boolean = false;

   tableForm: FormGroup;
   lang: any;
   value: any[] = [];
   displayValueTrue: any;
   tableRows: any[] = [];

   constructor(
      private translate: TranslateService,
      private eformService: EformService,
      private fb: FormBuilder
   ) {
   }

   ngOnInit(): void {
      this.lang = this.eformService.selectedLanguage;
      this.initializeTableForm();
   }

   initializeTableForm(): void {
      this.tableForm = this.fb.group({});

      if (this.field.columns) {
         this.addFormControlsForNewRow(this.field.columns);
      }

      // เพิ่ม tableFormGroup เข้าไปใน parent form
      if (this.form && this.fieldName) {
         this.form.addControl(this.fieldName, this.tableForm);
      }

      if (this.tableRows.length === 0) {
         this.tableRows.push({ columns: this.field.columns });
      }
   }

   private addFormControlsForNewRow(columns: any[]) {
      const tableControls = {}
      columns.forEach((column, columnIndex) => {
         const fieldName = this.field.fieldName || this.field.fieldNameEmpty;
         column.fieldNameEmpty = fieldName + columnIndex;
         const controlName = column.fieldName || column.fieldNameEmpty;

         let regex = '';
         switch (column.fieldType) {
            case 't-textbox': {
               if (column.display) {
                  tableControls[controlName] = column.required ? new FormControl('', [Validators.required, Validators.pattern(column.regex), Validators.maxLength(column.maxLength)])
                     : new FormControl('', Validators.pattern(column.regex));
               } else {
                  tableControls[controlName] = new FormControl(false, Validators.pattern(column.regex));
               }
               break;
            }
            case 't-radio': {
               const opts = {};
               if (column.display) {
                  this.getRadioOption(column.radioOption);
                  switch (column.type) {
                     case 'checkbox': {
                        opts[this.displayValueTrue] = new FormControl('');
                        tableControls[controlName] = new FormGroup(opts);
                        break;
                     }
                     default: {
                        tableControls[controlName] = column.required ? new FormControl('', [Validators.required]) : new FormControl('');
                        break;
                     }
                  }
               } else {
                  tableControls[controlName] = new FormControl(false);
               }
               break;
            }
            case 't-choice': {
               const opts = {};
               if (column.display) {
                  switch (column.type) {
                     case 'checkbox': {
                        let i = 0;
                        for (const otp of column.choices) {
                           const value = (typeof otp.value === 'boolean') ? otp.value : false;
                           opts[column.fieldName + '_' + i] = column.required ? new FormControl(value, [Validators.required])
                              : new FormControl('');
                           i++;
                        }
                        tableControls[controlName] = new FormGroup(opts, this.validateCheckbox(column.required));
                        break;
                     }
                     default: {
                        tableControls[controlName] = column.required ? new FormControl('', [Validators.required])
                           : new FormControl('');
                        break;
                     }
                  }
               } else {
                  tableControls[controlName] = new FormControl(false);
               }
               break;
            }
            case 't-email': {
               regex = '^(?!.*\\.{2})[\\w-]+(\\.[\\w-]+)*@(?!.*\\_)[\\w-]+(\\.[\\w-]+)*\\.[\\w-]{2,4}$';
               if (column.display) {
                  tableControls[controlName] = column.required ? new FormControl('', [Validators.required, Validators.pattern(regex)])
                     : new FormControl('', Validators.pattern(regex));
               } else {
                  tableControls[controlName] = new FormControl('', Validators.pattern(regex));
               }
               break;
            }
            case 't-website': {
               regex = '(?:http[s]?|ftp):\/\/[\\w_-]+(?:\\.[\\w_-]+)+(?:[\\w.,@?^=%&:/~+#-]*[\\w@?^=%&/~+#-])?';
               if (column.display) {
                  tableControls[controlName] = column.required ? new FormControl('', [Validators.required, Validators.pattern(regex)])
                     : new FormControl('', Validators.pattern(regex));
               } else {
                  tableControls[controlName] = new FormControl(false, Validators.pattern(regex));
               }
               break;
            }
            default: {
               if (column.display) {
                  tableControls[controlName] = column.required ? new FormControl('', [Validators.required])
                     : new FormControl('');
               } else {
                  tableControls[controlName] = new FormControl(false);
               }
               break;
            }
         }
         this.tableForm.addControl(controlName, tableControls[controlName]);
      });
   }

   getPlaceholder(column) {
      let placeholder = '';
      if (column.fieldType === 't-email') {
         placeholder = this.getPlaceHolderDefaultValue(column, 'example@email.com');
      } else if (column.fieldType === 't-website') {
         placeholder = this.getPlaceHolderDefaultValue(column, 'http://www.abc.com');
      } else {
         placeholder = this.getPlaceHolderDefaultValue(column, '');
      }
      return placeholder;
   }

   getPlaceHolderDefaultValue(column, defaultValue) {
      const { en, th, cn } = column.placeholder;
      switch (this.lang) {
         case 'th':
            return th !== undefined && th !== '' ? th : en || defaultValue;
         case 'cn':
            return cn !== undefined && cn !== '' ? cn : th || en || defaultValue;
         default:
            return en !== undefined && en !== '' ? en : th || defaultValue;
      }
   }

   getLabelName(column) {
      const { en, th, cn } = column.labelName;
      switch (this.lang) {
         case 'th':
            return th !== undefined && th !== '' ? th : en || null;
         case 'cn':
            return cn !== undefined && cn !== '' ? cn : th || en || null;
         default:
            return en !== undefined && en !== '' ? en : th || null;
      }
   }

   getChoice(column) {
      let choices = [];
      if (column.fieldType === 't-choice') {
         const objChoice = (column.choiceOption === 0) ? column.choices : column.choiceTmp;
         if (objChoice !== undefined) {
            for (let i = 0; i < objChoice.length; i++) {
               if (column.type === 'dropdown') {
                  this.insertDataChoice(choices, objChoice[i]);
               } else {
                  if (!objChoice[i].isPrompt) {
                     this.insertDataChoice(choices, objChoice[i]);
                  }
               }
            }
         }
      } else {
         choices = this.generateRadioChoices(column);
      }
      return choices;
   }

   getRadioOption(field) {
      switch (field) {
         case 0:
            this.displayValueTrue = 'yes';
            break;
         case 1:
            this.displayValueTrue = 'true';
            break;
         case 2:
            this.displayValueTrue = 'enable';
            break;
         case 3:
            this.displayValueTrue = 'on';
            break;
         case 4:
            this.displayValueTrue = 'read';
            break;
         default:
            this.displayValueTrue = 'yes';
            break;
      }

   }

   generateRadioChoices(column) {
      const choices = [];
      switch (column.readioOption) {
         case 0:
            choices.push({ label: 'Yes', value: 'true' }, { label: 'No', value: 'false' });
            break;
         case 1:
            choices.push({ label: 'True', value: 'true' }, { label: 'False', value: 'false' });
            break;
         case 2:
            choices.push({ label: 'Enable', value: 'true' }, { label: 'Disable', value: 'false' });
            break;
         case 3:
            choices.push({ label: 'On', value: 'true' }, { label: 'Off', value: 'false' });
            break;
         case 4:
            choices.push({ label: 'Read', value: 'true' }, { label: 'UnRead', value: 'false' });
            break;
         default:
            choices.push({ label: 'Yes', value: 'true' }, { label: 'No', value: 'false' });
            break;
      }
      return choices;
   }

   validateCheckbox(require: boolean): ValidatorFn {
      return function validate(formGroup: FormGroup) {
         let checked = 0;
         Object.keys(formGroup.controls).forEach(key => {
            const control = formGroup.controls[key];
            if (control.value) {
               checked++;
            }
         });
         if (require && checked < 1) {
            return {
               requireCheckboxToBeChecked: true,
            };
         }
         return null;
      };
   }

   insertDataChoice(choices, choice) {
      switch (this.lang) {
         case 'th': {
            choices.push({ label: choice.label.th, value: choice.value });
            break;
         }
         case 'cn': {
            choices.push({ label: choice.label.cn, value: choice.value });
            break;
         }
         default: {
            choices.push({ label: choice.label.en, value: choice.value });
         }
      }
   }

   addRow() {
      const arr = this.field.columns.map((column, index) => {
         const newColumn = { ...column };
         newColumn.labelName = {'en': '', 'th': '', 'cn': ''};
         const baseFieldName = this.field.fieldName || this.field.fieldNameEmpty;
         newColumn.fieldNameEmpty = `${baseFieldName}_row${this.tableRows.length}_${index}`;
         newColumn.fieldName = newColumn.fieldNameEmpty;
         return { ...newColumn, display: true };
      });
      
      this.tableRows.push({ columns: arr });
      this.addFormControlsForNewRow(arr);
   }

   deleteRow(index: number) {
      if (this.tableRows.length > 1) {
         const rowToDelete = this.tableRows[index];
         rowToDelete?.columns?.forEach(column => {
            const controlName = column.fieldName || column.fieldNameEmpty;
            if (this.tableForm.contains(controlName)) {
               this.tableForm.removeControl(controlName);
            }
         });
         
         this.tableRows.splice(index, 1);
      }
   }

   getFieldName(column: any): string {
      return column.fieldName || column.fieldNameEmpty;
   }

   isEmpty(value: any): boolean {
      return value == null || value === '';
   }

   getColumnType(column: any): string {
      return column.fieldType;
   }
}